import React, { useContext, createContext, useState, useEffect } from "react";
import { makeAuthedRequest } from "utils/makeAuthedRequest";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Loader from "components/Loader";
import Cookies from "js-cookie";
import mobile from "mobileBridge";
import { useRouteMatch } from "react-router-dom";
import environment from "../environment";

const AccountInfoContext = createContext();

// Account
export function useAccountInfo() {
  const { data, isLoading, refetch } = useQuery(
    ["account"],
    () =>
      makeAuthedRequest("v1/settings/account", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

// User
export function useUserInfo(userId) {
  const { data, isLoading, refetch } = useQuery(
    ["userInfo", userId],
    () =>
      makeAuthedRequest(`v1/settings/ibo/user/${userId || "1"}`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error((await res.json()).message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      // staleTime: Infinity,
      // cacheTime: Infinity,
      // enabled: !!userId,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

// Users
export function useUsersInfo() {
  const { data, isLoading, refetch } = useQuery(
    ["usersInfo"],
    () =>
      makeAuthedRequest("v1/settings/ibo/users", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error((await res.json()).message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

// States
export function useStatesInfo() {
  const { data, isLoading, refetch } = useQuery(
    ["stateInfo"],
    () =>
      makeAuthedRequest("v1/settings/states", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error((await res.json()).message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

// IBOLOA
export function useIBOLOAInfo() {
  const { data, isLoading, refetch } = useQuery(
    ["iboInfo"],
    () =>
      makeAuthedRequest("v1/settings/ibo-info/loa", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error((await res.json()).message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

// Management Team Hooks
export function useManagementTeam() {
  const queryClient = useQueryClient();

  const { data, status, isLoading, refetch } = useQuery(
    ["mt-settings"],
    () =>
      makeAuthedRequest("v1/settings/mt-settings", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }).catch((err) => {
        if (environment.localDevMode) return null;
        throw err;
      }),
    {
      enabled: environment.WEB_APP_COUNTRY !== "AU",
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const updateCanAccessMT = useMutation(
    (data) =>
      makeAuthedRequest("v1/settings/mt-settings", {
        method: "POST",
        body: JSON.stringify(data),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("mt-settings");
      },
    }
  );

  return {
    data,
    status,
    isLoading,
    refetch,
    updateCanAccessMT,
  };
}

// SubEntityNagScreen
export function useSubEntityNagScreen() {
  const { data, status, isLoading, refetch } = useQuery(
    ["nag-screens"],
    () =>
      makeAuthedRequest("v1/settings/sub-entity/nag-screen", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    status,
    isLoading,
    refetch,
  };
}

// Privacy
export function usePrivacy() {
  const queryClient = useQueryClient();

  const { data, status, isLoading, refetch } = useQuery(
    ["tc-agree"],
    () =>
      makeAuthedRequest("v1/settings/tc-agree", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const tcAgreePost = useMutation(
    (data) =>
      makeAuthedRequest("v1/settings/tc-agree", {
        method: "POST",
        body: JSON.stringify(data),
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("tc-agree");
      },
    }
  );

  return {
    data,
    status,
    isLoading,
    refetch,
    tcAgreePost,
  };
}

// Issues (Email/Phone Verifications)
export function useIssues(contactId) {
  const { data, status, error, isLoading, refetch } = useQuery(
    ["issues", contactId],
    () =>
      makeAuthedRequest(`v1/settings/issues/${contactId}`, {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      enabled: !!contactId,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    status,
    isLoading,
    error,
    refetch,
  };
}

export function useBilling() {
  const queryClient = useQueryClient();

  const { data, status, isLoading, refetch } = useQuery(
    ["billing"],
    () =>
      makeAuthedRequest("v1/settings/pending-monthly-billing", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.text();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    status,
    isLoading,
    // error,
    refetch,
  };
}

// SubEntity
export function useSubEntity() {
  const { data, isLoading, refetch } = useQuery(
    ["subEntityInfo"],
    () =>
      makeAuthedRequest("v1/settings/sub-entity", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error((await res.json()).message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

// approved provider consent data
export function useConsent() {
  const { data, isLoading } = useQuery(
    ["consent"],
    async () =>
      makeAuthedRequest("v1/settings/amway", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
  };
}

// Cached IBO Info
export function useCachedIBOInfo() {
  const { data, isLoading, refetch } = useQuery(
    ["cachedIBOInfo"],
    () =>
      makeAuthedRequest("v1/settings/cached-ibo-info", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  return {
    data,
    isLoading,
    refetch,
  };
}

export const AccountInfoProvider = ({ children }) => {
  const match = useRouteMatch("/user/:userId");
  const userId = match?.params?.userId;

  const [storedUserId, setStoredUserId] = useState(null);

  const [nativeUserMobileResponse, setNativeUserMobileResponse] =
    useState(null);

  useEffect(() => {
    mobile.isApp() &&
      mobile.postMessage("get-native-user", null, true).then((id) => {
        return setNativeUserMobileResponse(id);
      });
  }, []);

  useEffect(() => {
    if (userId) {
      setStoredUserId(userId);
    }
  }, [userId]);

  const queryClient = useQueryClient();
  const [cookieUserId, setCookieUserId] = useState(
    Cookies.get("community-user-id") || ""
  );

  // Determine the effective user ID to use for queries
  // Priority: storedUserId (from route) > nativeUserMobileResponse (from mobile bridge) > cookieUserId
  const effectiveUserId = mobile.isApp()
    ? storedUserId || nativeUserMobileResponse || cookieUserId
    : cookieUserId;

  const {
    data: accountData,
    isLoading: isAccountLoading,
    refetch: refetchAccountData,
  } = useAccountInfo();

  const {
    data: userData,
    isLoading: isUserLoading,
    refetch: refetchUserData,
  } = useUserInfo(effectiveUserId);

  const {
    data: usersData,
    isLoading: isUsersLoading,
    refetch: refetchUsersData,
  } = useUsersInfo();

  const { data: statesData, isLoading: isStatesLoading } = useStatesInfo();
  const { data: iboLOAData, isLoading: isIBOLOALoading } = useIBOLOAInfo();
  const {
    data: managementTeamData,
    isLoading: isManagementTeamLoading,
    updateCanAccessMT,
  } = useManagementTeam();

  const { data: subEntityData, isLoading: isSubEntityLoading } = useSubEntity();
  const { data: nagScreenData, isLoading: isNagScreenLoading } =
    useSubEntityNagScreen();

  const {
    data: privacyData,
    isLoading: isPrivacyLoading,
    tcAgreePost,
  } = usePrivacy();

  const { data: consentData, isLoading: isConsentLoading } = useConsent();

  const { data: billingData, isLoading: isBillingLoading } = useBilling();

  const { data: cachedIBOInfo, isLoading: isCachedIBOInfoLoading } =
    useCachedIBOInfo();

  const [canAccessMT, setCanAccessMT] = useState(false);
  const [allowFaceAuth, setAllowFaceAuth] = useState(false);
  const [pinCreatedOn, setPinCreatedOn] = useState(null);

  const [directToTCPage, setDirectToTCPage] = useState(true);
  const [directToSubEntityNagScreen, setDirectToSubEntityNagScreen] =
    useState(true);
  const [directToVerifyNagScreen, setDirectToVerifyNagScreen] = useState(true);

  const [directToConsentNagScreen, setDirectToConsentNagScreen] =
    useState(true);

  const [activeUser, setActiveUser] = useState({
    contactId: null,
    userId: null,
    name: null,
    firstName: null,
    lastName: null,
    middleName: null,
    isEmailVerified: null,
    isPhoneVerified: null,
    phone: null,
    iboNum: null,
    email: null,
    iboLOAInfo: null,
    accountIssues: null,
  });

  const {
    data: issuesData,
    isLoading: isIssuesLoading,
    refetch: refetchIssuesData,
  } = useIssues(activeUser.contactId);

  // Find the activeUser
  useEffect(() => {
    if (usersData && accountData && accountData.contacts) {
      // console.log(storedUserId);
      // console.log(cookieUserId);
      let matchedUser;

      if (mobile.isApp()) {
        if (storedUserId) {
          matchedUser = usersData.find(
            (user) => user.userId.toString() == storedUserId
          );
        } else if (nativeUserMobileResponse) {
          matchedUser = usersData.find(
            (user) => user.userId.toString() == nativeUserMobileResponse
          );
        } else if (cookieUserId) {
          matchedUser = usersData.find(
            (user) => user.userId.toString() === cookieUserId
          );
        }
      } else if (cookieUserId) {
        matchedUser = usersData.find(
          (user) => user.userId === parseInt(cookieUserId)
        );
      }

      if (!matchedUser) {
        matchedUser = usersData.find((user) => user.isCurrent);
      }

      if (!matchedUser && usersData.length > 0) {
        // console.log("DEFAULTING TO FIRST USER");

        matchedUser = usersData[0];
      }

      if (matchedUser) {
        const matchedContact = accountData.contacts.find(
          (contact) => contact.contactId === matchedUser.contactId
        );

        if (matchedContact) {
          setActiveUser({
            contactId: matchedContact.contactId,
            userId: matchedUser.userId,
            name: `${matchedContact.firstName} ${matchedContact.lastName}`,
            firstName: matchedContact.firstName,
            lastName: matchedContact.lastName,
            middleName: matchedContact.middleName,
            isEmailVerified: matchedContact.isEmailVerified,
            isPhoneVerified: matchedContact.isPhoneVerified,
            phone: matchedContact.phone,
            iboNum: accountData.account.ibo,
            email: matchedContact.email,
            iboLOAInfo: iboLOAData,
            accountIssues: issuesData,
          });
          Cookies.set("community-user-id", matchedUser.userId, {
            domain: ".wwdb.com",
            path: "/",
          });
        }
      }
    }
  }, [accountData, usersData, iboLOAData, nativeUserMobileResponse]);

  // Set cookie when switching user
  const setActiveUserInfo = async (user) => {
    setActiveUser(user);

    // console.log("Setting active user:");
    // console.log(user);

    if (user?.userId) {
      Cookies.set("community-user-id", user.userId, {
        domain: ".wwdb.com",
        path: "/",
      });
      setCookieUserId(user.userId.toString());
    } else {
      Cookies.remove("community-user-id", { expires: -1 });
      setCookieUserId("");
    }
    queryClient.invalidateQueries(["issues", user.contactId]);
    queryClient.invalidateQueries(["userInfo", user.userId]);
    queryClient.invalidateQueries(["iboInfo"]);
  };

  const invalidateSubEntity = () => {
    queryClient.invalidateQueries("subEntityInfo");
  };

  useEffect(() => {
    if (managementTeamData) {
      setCanAccessMT(managementTeamData.canAccessMT);
      setAllowFaceAuth(managementTeamData.allowFaceTouch);
      setPinCreatedOn(managementTeamData.pinCreatedOn);
    }
  }, [managementTeamData]);

  const [checksLoading, setChecksLoading] = useState(true);

  // issuesData, nagScreenData, privacyData, consentData
  useEffect(() => {
    if (
      isIssuesLoading ||
      isNagScreenLoading ||
      isPrivacyLoading ||
      isConsentLoading
    ) {
      setChecksLoading(true);
    } else {
      setChecksLoading(false);
    }
  }, [isIssuesLoading, isNagScreenLoading, isPrivacyLoading, isConsentLoading]);

  const getSessionStorageRoutes = () => {
    const prevUrl = sessionStorage.getItem("prevUrl");

    const removeSessionStorageRoute = (key) => {
      sessionStorage.removeItem(key);
    };

    return {
      prevUrl,
      removeSessionStorageRoute,
    };
  };

  const getReceipt = useMutation((data) =>
    makeAuthedRequest(`v1/settings/receipt/${data.type}/${data.referenceId}`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  if (
    isAccountLoading ||
    isUserLoading ||
    isStatesLoading ||
    isUsersLoading ||
    isSubEntityLoading ||
    isIBOLOALoading ||
    isIssuesLoading ||
    isManagementTeamLoading ||
    isBillingLoading ||
    isNagScreenLoading ||
    isPrivacyLoading ||
    isUserLoading ||
    isConsentLoading ||
    isCachedIBOInfoLoading ||
    isSubEntityLoading
  ) {
    return (
      <div>
        <Loader color={"#415364"} size={85} />
      </div>
    );
  }

  return (
    <AccountInfoContext.Provider
      value={{
        accountData,
        userData,
        usersData,
        refetchUsersData,
        activeUser,
        setActiveUserInfo,
        refetchUserData,
        statesData,
        issuesData,
        refetchIssuesData,
        canAccessMT,
        allowFaceAuth,
        pinCreatedOn,
        updateCanAccessMT,
        setAllowFaceAuth,
        billingData,
        refetchAccountData,
        subEntityData,
        invalidateSubEntity,
        nagScreenData,
        privacyData,
        tcAgreePost,
        consentData,
        directToTCPage,
        setDirectToTCPage,
        directToSubEntityNagScreen,
        setDirectToSubEntityNagScreen,
        directToVerifyNagScreen,
        setDirectToVerifyNagScreen,
        directToConsentNagScreen,
        setDirectToConsentNagScreen,
        iboLOAData,
        cachedIBOInfo,
        setStoredUserId,
        checksLoading,
        getSessionStorageRoutes,
        getReceipt,
      }}
    >
      {children}
    </AccountInfoContext.Provider>
  );
};

export const useAccountInfoContext = () => useContext(AccountInfoContext);
export const useAccount = () => useContext(AccountInfoContext);
